import { useBehavior, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, ElementType, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { TTextConsumerData } from '../../../types/data';
import { TTextOption } from '../../../types/options';
import { getTextSizeVariant } from '../../../utils/helpers';
import { capitalText, getInlineStyles } from '../../../utils/styles';
import { Highlighter } from './Highlighter';
import styles from './TextCommon.module.css';

type TTextProps<TD, TO> = (
    | { isWidget: true; widget: TWidget<TD, TO> }
    | {
          isWidget: false;
          uid?: string;
          options?: TTextOption;
          textTransformation?: TTextConsumerData['textTransformation'];
          content?: string;
      }
) & {
    children?: ReactNode;
    as?: ElementType;
};

const TextCommon = (props: TTextProps<TTextConsumerData, TTextOption>) => {
    const textRef = useRef<HTMLElement>(null);

    const finalWidget: TWidget<TTextConsumerData, TTextOption> = props.isWidget
        ? props.widget
        : ({
              uid: props.uid,
              options: props.options,
              data: {
                  text: props.content,
                  textTransformation: props.textTransformation,
              },
          } as TWidget<TTextConsumerData, TTextOption>);

    const { triggerActionsByNameAndType } = useTriggerAction(finalWidget);
    const { isHidden } = useBehavior(finalWidget);

    const content = finalWidget.data?.text;
    const ComponentToRender = props.as || 'p';

    const {
        variant: optionVariant = 'main',
        textAlign: optionTextAlign = 'start',
        fontWeight: optionFontWeight = 'regular',
        smallCaps,
        ellipsis,
        lineThrough,
        textColor,
        enableAutoResize,
        maxLines,
    } = finalWidget.options || {};

    const [effectiveVariant, setEffectiveVariant] = useState(optionVariant);

    useLayoutEffect(() => {
        const element = textRef.current;
        if (!element || !enableAutoResize || !maxLines || !content) {
            return;
        }

        setEffectiveVariant(getTextSizeVariant(optionVariant, maxLines, styles, element));
    }, [content, enableAutoResize, maxLines, optionVariant]);

    const classNames = clsx(
        styles.textCommon,
        effectiveVariant && styles[effectiveVariant],
        optionTextAlign && styles[`textAlign${capitalText(optionTextAlign)}`],
        optionFontWeight && !(effectiveVariant === 'tag' || effectiveVariant === 'subheader') && styles[`fontWeight${capitalText(optionFontWeight)}`],
        smallCaps && styles.smallCaps,
        ellipsis && !enableAutoResize && styles.ellipsis,
        lineThrough && styles.lineThrough,
        textColor,
    );

    const onTextClick = (actionName: string) => {
        if (actionName) {
            triggerActionsByNameAndType(ActionActionTypeEnum.onClick, actionName).then();
        }
    };

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <ComponentToRender
                ref={textRef}
                data-test={finalWidget.uid}
                aria-label={content}
                className={classNames}
                style={getInlineStyles({ color: textColor, margin: finalWidget.options?.margin })}
            >
                {content && <Highlighter content={content} textTransformation={finalWidget.data?.textTransformation} onClickAction={onTextClick} />}
                {props.children}
            </ComponentToRender>
        </Activity>
    );
};
export { TextCommon };
